/**
 * Landlord Onboarding List Component
 * Displays all landlord onboardings with filtering and status tracking
 */

import React from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Button,
  Flex,
  Text,
  Select,
  Progress,
  Icon,
  Tooltip,
  HStack,
  Avatar,
  VStack,
  Spinner,
} from '@chakra-ui/react';
import {
  MdOutlineRefresh,
  MdOutlineWarning,
  MdOutlineCheckCircle,
  MdOutlineSchedule,
} from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import type {
  LandlordOnboarding,
  LandlordOnboardingStatus,
} from '../../../types/landlordOnboarding';
import {
  getStageDisplayName,
  getStageOrder,
} from '../../../types/landlordOnboarding';

interface LandlordOnboardingListProps {
  onboardings: LandlordOnboarding[];
  loading: boolean;
  filter: LandlordOnboardingStatus | 'all';
  onFilterChange: (filter: LandlordOnboardingStatus | 'all') => void;
  onSelectOnboarding: (onboarding: LandlordOnboarding) => void;
  onRefresh: () => void;
}

const LandlordOnboardingList: React.FC<LandlordOnboardingListProps> = ({
  onboardings,
  loading,
  filter,
  onFilterChange,
  onSelectOnboarding,
  onRefresh,
}) => {
  const { t } = useTranslation('landlordOnboarding');

  // Get status badge color
  const getStatusColor = (status: LandlordOnboardingStatus): string => {
    const colors: Record<LandlordOnboardingStatus, string> = {
      not_started: 'gray',
      in_progress: 'blue',
      completed: 'green',
      blocked: 'red',
      abandoned: 'orange',
    };
    return colors[status];
  };

  // Get status icon
  const getStatusIcon = (status: LandlordOnboardingStatus) => {
    const icons: Record<LandlordOnboardingStatus, any> = {
      not_started: MdOutlineSchedule,
      in_progress: MdOutlineSchedule,
      completed: MdOutlineCheckCircle,
      blocked: MdOutlineWarning,
      abandoned: MdOutlineWarning,
    };
    return icons[status];
  };

  // Format date
  const formatDate = (timestamp: any): string => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Format time ago
  const formatTimeAgo = (timestamp: any): string => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    
    if (diffDays > 0) {
      return `${diffDays}d ago`;
    } else if (diffHours > 0) {
      return `${diffHours}h ago`;
    } else {
      return 'Just now';
    }
  };

  return (
    <Box>
      {/* Filters */}
      <Flex justify="space-between" align="center" mb={4}>
        <HStack spacing={3}>
          <Text fontSize="sm" fontWeight="medium" color="gray.600">
            {t('list.filter')}:
          </Text>
          <Select
            size="sm"
            w="200px"
            value={filter}
            onChange={(e) =>
              onFilterChange(e.target.value as LandlordOnboardingStatus | 'all')
            }
          >
            <option value="all">{t('list.allStatuses')}</option>
            <option value="not_started">
              {t('status.notStarted')}
            </option>
            <option value="in_progress">
              {t('status.inProgress')}
            </option>
            <option value="completed">
              {t('status.completed')}
            </option>
            <option value="blocked">
              {t('status.blocked')}
            </option>
            <option value="abandoned">
              {t('status.abandoned')}
            </option>
          </Select>
        </HStack>

        <Button
          size="sm"
          leftIcon={<Icon as={MdOutlineRefresh} />}
          onClick={onRefresh}
          isLoading={loading}
        >
          {t('common.refresh')}
        </Button>
      </Flex>

      {/* Table */}
      {loading ? (
        <Flex justify="center" align="center" minH="300px">
          <Spinner size="lg" color="blue.500" />
        </Flex>
      ) : onboardings.length === 0 ? (
        <Flex justify="center" align="center" minH="300px">
          <VStack spacing={2}>
            <Text color="gray.500">
              {t('list.noOnboardings')}
            </Text>
            <Text fontSize="sm" color="gray.400">
              {filter !== 'all'
                ? t('list.tryDifferentFilter')
                : t('list.createFirst')}
            </Text>
          </VStack>
        </Flex>
      ) : (
        <Box overflowX="auto">
          <Table variant="simple" size="sm">
            <Thead>
              <Tr>
                <Th>{t('list.landlord')}</Th>
                <Th>{t('list.status')}</Th>
                <Th>{t('list.currentStage')}</Th>
                <Th>{t('list.progress')}</Th>
                <Th>{t('list.properties')}</Th>
                <Th>{t('list.assignedTo')}</Th>
                <Th>{t('list.started')}</Th>
                <Th>{t('list.target')}</Th>
                <Th>{t('list.actions')}</Th>
              </Tr>
            </Thead>
            <Tbody>
              {onboardings.map((onboarding) => (
                <Tr
                  key={onboarding.id}
                  _hover={{ bg: 'gray.50', cursor: 'pointer' }}
                  onClick={() => onSelectOnboarding(onboarding)}
                >
                  {/* Landlord */}
                  <Td>
                    <HStack spacing={3}>
                      <Avatar
                        size="sm"
                        name={onboarding.landlordName}
                        src={undefined}
                      />
                      <VStack align="start" spacing={0}>
                        <Text fontWeight="medium" fontSize="sm">
                          {onboarding.landlordName}
                        </Text>
                        <Text fontSize="xs" color="gray.600">
                          {onboarding.landlordEmail}
                        </Text>
                        {onboarding.landlordCompany && (
                          <Text fontSize="xs" color="gray.500">
                            {onboarding.landlordCompany}
                          </Text>
                        )}
                      </VStack>
                    </HStack>
                  </Td>

                  {/* Status */}
                  <Td>
                    <Badge
                      colorScheme={getStatusColor(onboarding.status)}
                      display="flex"
                      alignItems="center"
                      gap={1}
                      w="fit-content"
                    >
                      <Icon as={getStatusIcon(onboarding.status)} />
                      {t(`landlordOnboarding.status.${onboarding.status}`)}
                    </Badge>
                    {onboarding.blockers.filter(b => !b.resolvedAt).length > 0 && (
                      <Tooltip
                        label={t('list.hasBlockers', {
                          count: onboarding.blockers.filter(b => !b.resolvedAt)
                            .length,
                        })}
                      >
                        <Badge colorScheme="red" ml={1}>
                          {onboarding.blockers.filter(b => !b.resolvedAt).length}
                        </Badge>
                      </Tooltip>
                    )}
                  </Td>

                  {/* Current Stage */}
                  <Td>
                    <VStack align="start" spacing={0}>
                      <Text fontSize="sm" fontWeight="medium">
                        {getStageDisplayName(onboarding.currentStage)}
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        Stage {getStageOrder(onboarding.currentStage)} of 5
                      </Text>
                    </VStack>
                  </Td>

                  {/* Progress */}
                  <Td>
                    <VStack align="start" spacing={1} minW="100px">
                      <Progress
                        value={onboarding.progress}
                        size="sm"
                        colorScheme={
                          onboarding.progress === 100
                            ? 'green'
                            : onboarding.progress > 50
                            ? 'blue'
                            : 'orange'
                        }
                        w="100%"
                      />
                      <Text fontSize="xs" color="gray.600">
                        {onboarding.progress}%
                      </Text>
                    </VStack>
                  </Td>

                  {/* Properties */}
                  <Td>
                    <VStack align="start" spacing={0}>
                      <Text fontSize="sm">
                        {onboarding.propertyCount}{' '}
                        {onboarding.propertyCount === 1
                          ? t('list.property')
                          : t('list.properties')}
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        {onboarding.unitCount}{' '}
                        {onboarding.unitCount === 1
                          ? t('list.unit')
                          : t('list.units')}
                        , {onboarding.tenantCount}{' '}
                        {onboarding.tenantCount === 1
                          ? t('list.tenant')
                          : t('list.tenants')}
                      </Text>
                    </VStack>
                  </Td>

                  {/* Assigned To */}
                  <Td>
                    {onboarding.assignedToName ? (
                      <HStack spacing={2}>
                        <Avatar size="xs" name={onboarding.assignedToName} />
                        <Text fontSize="sm">{onboarding.assignedToName}</Text>
                      </HStack>
                    ) : (
                      <Text fontSize="sm" color="gray.400">
                        {t('list.unassigned')}
                      </Text>
                    )}
                  </Td>

                  {/* Started */}
                  <Td>
                    <VStack align="start" spacing={0}>
                      <Text fontSize="sm">
                        {formatDate(onboarding.createdAt)}
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        {formatTimeAgo(onboarding.createdAt)}
                      </Text>
                    </VStack>
                  </Td>

                  {/* Target Completion */}
                  <Td>
                    <VStack align="start" spacing={0}>
                      <Text fontSize="sm">
                        {formatDate(onboarding.targetCompletionDate)}
                      </Text>
                      {onboarding.actualCompletionDate ? (
                        <Badge colorScheme="green" fontSize="xs">
                          {t('list.completed')}
                        </Badge>
                      ) : (
                        <Text fontSize="xs" color="gray.500">
                          {/* Calculate days until target */}
                          {(() => {
                            const target = onboarding.targetCompletionDate.toDate
                              ? onboarding.targetCompletionDate.toDate()
                              : new Date(onboarding.targetCompletionDate as any);
                            const now = new Date();
                            const diffDays = Math.ceil(
                              (target.getTime() - now.getTime()) /
                                (1000 * 60 * 60 * 24)
                            );
                            if (diffDays < 0) {
                              return (
                                <Text fontSize="xs" color="red.500">
                                  {Math.abs(diffDays)}d overdue
                                </Text>
                              );
                            } else if (diffDays === 0) {
                              return (
                                <Text fontSize="xs" color="orange.500">
                                  Due today
                                </Text>
                              );
                            } else {
                              return `${diffDays}d remaining`;
                            }
                          })()}
                        </Text>
                      )}
                    </VStack>
                  </Td>

                  {/* Actions */}
                  <Td>
                    <Button
                      size="sm"
                      variant="outline"
                      colorScheme="blue"
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelectOnboarding(onboarding);
                      }}
                    >
                      {t('common.view')}
                    </Button>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}
    </Box>
  );
};

export default LandlordOnboardingList;
